//#region src/screen-size/ScreenSizeService.ts
/** Default breakpoint thresholds (Tailwind CSS defaults) */
var DEFAULT_BREAKPOINTS = {
	xs: 0,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280
};
/** Ordered list of breakpoints from smallest to largest */
var BREAKPOINT_ORDER = [
	"xs",
	"sm",
	"md",
	"lg",
	"xl"
];
var ScreenSizeServiceImpl = class {
	constructor(breakpoints = DEFAULT_BREAKPOINTS) {
		this._listeners = /* @__PURE__ */ new Set();
		this._currentBreakpoint = "lg";
		this._width = 0;
		this._handleResize = () => {
			const oldBreakpoint = this._currentBreakpoint;
			this._updateSize();
			if (oldBreakpoint !== this._currentBreakpoint) this._notifyListeners();
		};
		this._breakpoints = breakpoints;
		if (typeof window !== "undefined") {
			this._updateSize();
			window.addEventListener("resize", this._handleResize);
		}
	}
	/** Current breakpoint name */
	get breakpoint() {
		return this._currentBreakpoint;
	}
	/** Current viewport width in pixels */
	get width() {
		return this._width;
	}
	/** True if viewport is extra small or small (< 768px) */
	get isMobile() {
		return this._currentBreakpoint === "xs" || this._currentBreakpoint === "sm";
	}
	/** True if viewport is medium (768px - 1023px) */
	get isTablet() {
		return this._currentBreakpoint === "md";
	}
	/** True if viewport is large or extra large (>= 1024px) */
	get isDesktop() {
		return this._currentBreakpoint === "lg" || this._currentBreakpoint === "xl";
	}
	/** Breakpoint order for fallback resolution */
	get breakpointOrder() {
		return BREAKPOINT_ORDER;
	}
	/**
	* Subscribe to breakpoint changes.
	* Callback is called when the breakpoint changes (not on every resize).
	*
	* @param callback - Function to call when breakpoint changes
	* @returns Unsubscribe function
	*/
	subscribe(callback) {
		this._listeners.add(callback);
		return () => this._listeners.delete(callback);
	}
	/**
	* Check if the current breakpoint matches the given breakpoint.
	*/
	is(breakpoint) {
		return this._currentBreakpoint === breakpoint;
	}
	/**
	* Check if the current breakpoint is at least the given breakpoint.
	* E.g., isAtLeast('md') returns true for md, lg, xl.
	*/
	isAtLeast(breakpoint) {
		return BREAKPOINT_ORDER.indexOf(this._currentBreakpoint) >= BREAKPOINT_ORDER.indexOf(breakpoint);
	}
	/**
	* Check if the current breakpoint is at most the given breakpoint.
	* E.g., isAtMost('md') returns true for xs, sm, md.
	*/
	isAtMost(breakpoint) {
		return BREAKPOINT_ORDER.indexOf(this._currentBreakpoint) <= BREAKPOINT_ORDER.indexOf(breakpoint);
	}
	_updateSize() {
		this._width = window.innerWidth;
		const bp = this._breakpoints;
		if (this._width >= bp.xl) this._currentBreakpoint = "xl";
		else if (this._width >= bp.lg) this._currentBreakpoint = "lg";
		else if (this._width >= bp.md) this._currentBreakpoint = "md";
		else if (this._width >= bp.sm) this._currentBreakpoint = "sm";
		else this._currentBreakpoint = "xs";
	}
	_notifyListeners() {
		this._listeners.forEach((cb) => cb());
	}
};
/** Singleton instance of the screen size service */
var screenSizeService = new ScreenSizeServiceImpl();
//#endregion
export { screenSizeService };
